import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const BandConfigPanel = ({ band, onSave, onClose, isVisible }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    department: '',
    jobLevel: '',
    minSalary: '',
    maxSalary: '',
    currency: 'EUR',
    effectiveDate: '',
    endDate: '',
    description: '',
    progressionRules: {
      annualIncrease: '',
      maxProgression: '',
      performanceMultiplier: ''
    },
    complianceSettings: {
      autoFlag: true,
      gapThreshold: 5,
      reviewFrequency: 'quarterly'
    }
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (band) {
      setFormData({
        name: band?.name || '',
        department: band?.department || '',
        jobLevel: band?.jobLevel || '',
        minSalary: band?.minSalary?.toString() || '',
        maxSalary: band?.maxSalary?.toString() || '',
        currency: band?.currency || 'EUR',
        effectiveDate: band?.effectiveDate || '',
        endDate: band?.endDate || '',
        description: band?.description || '',
        progressionRules: {
          annualIncrease: band?.progressionRules?.annualIncrease?.toString() || '',
          maxProgression: band?.progressionRules?.maxProgression?.toString() || '',
          performanceMultiplier: band?.progressionRules?.performanceMultiplier?.toString() || ''
        },
        complianceSettings: {
          autoFlag: band?.complianceSettings?.autoFlag ?? true,
          gapThreshold: band?.complianceSettings?.gapThreshold || 5,
          reviewFrequency: band?.complianceSettings?.reviewFrequency || 'quarterly'
        }
      });
    } else {
      // Reset form for new band
      setFormData({
        name: '',
        department: '',
        jobLevel: '',
        minSalary: '',
        maxSalary: '',
        currency: 'EUR',
        effectiveDate: new Date()?.toISOString()?.split('T')?.[0],
        endDate: '',
        description: '',
        progressionRules: {
          annualIncrease: '',
          maxProgression: '',
          performanceMultiplier: ''
        },
        complianceSettings: {
          autoFlag: true,
          gapThreshold: 5,
          reviewFrequency: 'quarterly'
        }
      });
    }
    setErrors({});
  }, [band]);

  const departmentOptions = [
    { value: 'engineering', label: 'Engineering' },
    { value: 'sales', label: 'Sales' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'finance', label: 'Finance' },
    { value: 'operations', label: 'Operations' },
    { value: 'legal', label: 'Legal' },
    { value: 'customer-success', label: 'Customer Success' }
  ];

  const jobLevelOptions = [
    { value: 'entry', label: 'Entry Level' },
    { value: 'junior', label: 'Junior' },
    { value: 'mid', label: 'Mid Level' },
    { value: 'senior', label: 'Senior' },
    { value: 'lead', label: 'Lead' },
    { value: 'principal', label: 'Principal' },
    { value: 'director', label: 'Director' },
    { value: 'vp', label: 'Vice President' },
    { value: 'c-level', label: 'C-Level' }
  ];

  const currencyOptions = [
    { value: 'EUR', label: 'EUR (Euro)' },
    { value: 'USD', label: 'USD (US Dollar)' },
    { value: 'GBP', label: 'GBP (British Pound)' },
    { value: 'SEK', label: 'SEK (Swedish Krona)' },
    { value: 'DKK', label: 'DKK (Danish Krone)' },
    { value: 'PLN', label: 'PLN (Polish Zloty)' }
  ];

  const reviewFrequencyOptions = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'biannual', label: 'Bi-Annual' },
    { value: 'annual', label: 'Annual' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev?.[parent],
        [field]: value
      }
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Band name is required';
    }

    if (!formData?.department) {
      newErrors.department = 'Department is required';
    }

    if (!formData?.jobLevel) {
      newErrors.jobLevel = 'Job level is required';
    }

    if (!formData?.minSalary || isNaN(formData?.minSalary) || Number(formData?.minSalary) <= 0) {
      newErrors.minSalary = 'Valid minimum salary is required';
    }

    if (!formData?.maxSalary || isNaN(formData?.maxSalary) || Number(formData?.maxSalary) <= 0) {
      newErrors.maxSalary = 'Valid maximum salary is required';
    }

    if (Number(formData?.minSalary) >= Number(formData?.maxSalary)) {
      newErrors.maxSalary = 'Maximum salary must be greater than minimum salary';
    }

    if (!formData?.effectiveDate) {
      newErrors.effectiveDate = 'Effective date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      const bandData = {
        ...formData,
        minSalary: Number(formData?.minSalary),
        maxSalary: Number(formData?.maxSalary),
        progressionRules: {
          ...formData?.progressionRules,
          annualIncrease: formData?.progressionRules?.annualIncrease ? Number(formData?.progressionRules?.annualIncrease) : null,
          maxProgression: formData?.progressionRules?.maxProgression ? Number(formData?.progressionRules?.maxProgression) : null,
          performanceMultiplier: formData?.progressionRules?.performanceMultiplier ? Number(formData?.progressionRules?.performanceMultiplier) : null
        }
      };

      await onSave(bandData);
    } catch (error) {
      console.error('Error saving band:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 lg:relative lg:bg-transparent lg:backdrop-blur-none">
      <div className="fixed inset-y-0 right-0 w-full lg:w-96 bg-card border-l border-border shadow-elevated lg:relative lg:shadow-soft lg:rounded-lg lg:border">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                {band ? 'Edit Pay Band' : 'Create New Pay Band'}
              </h3>
              <p className="text-sm text-muted-foreground">
                Configure salary range and compliance settings
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>

          {/* Form Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="font-medium text-foreground">Basic Information</h4>
                
                <Input
                  label={t('payBandsPage.config.bandName')}
                  type="text"
                  placeholder={t('payBandsPage.config.bandNamePlaceholder')}
                  value={formData?.name}
                  onChange={(e) => handleInputChange('name', e?.target?.value)}
                  error={errors?.name}
                  required
                />

                <Select
                  label="Department"
                  options={departmentOptions}
                  value={formData?.department}
                  onChange={(value) => handleInputChange('department', value)}
                  error={errors?.department}
                  required
                />

                <Select
                  label={t('payBandsPage.config.jobLevel')}
                  options={jobLevelOptions}
                  value={formData?.jobLevel}
                  onChange={(value) => handleInputChange('jobLevel', value)}
                  error={errors?.jobLevel}
                  required
                />

                <Input
                  label="Description"
                  type="text"
                  placeholder={t('payBandsPage.config.roleDescriptionPlaceholder')}
                  value={formData?.description}
                  onChange={(e) => handleInputChange('description', e?.target?.value)}
                />
              </div>

              {/* Salary Range */}
              <div className="space-y-4">
                <h4 className="font-medium text-foreground">Salary Range</h4>
                
                <Select
                  label="Currency"
                  options={currencyOptions}
                  value={formData?.currency}
                  onChange={(value) => handleInputChange('currency', value)}
                  required
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label={t('payBandsPage.config.minSalary')}
                    type="number"
                    placeholder="50000"
                    value={formData?.minSalary}
                    onChange={(e) => handleInputChange('minSalary', e?.target?.value)}
                    error={errors?.minSalary}
                    required
                  />

                  <Input
                    label={t('payBandsPage.config.maxSalary')}
                    type="number"
                    placeholder="80000"
                    value={formData?.maxSalary}
                    onChange={(e) => handleInputChange('maxSalary', e?.target?.value)}
                    error={errors?.maxSalary}
                    required
                  />
                </div>
              </div>

              {/* Effective Dates */}
              <div className="space-y-4">
                <h4 className="font-medium text-foreground">Effective Period</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label={t('payBandsPage.config.effectiveDate')}
                    type="date"
                    value={formData?.effectiveDate}
                    onChange={(e) => handleInputChange('effectiveDate', e?.target?.value)}
                    error={errors?.effectiveDate}
                    required
                  />

                  <Input
                    label={t('payBandsPage.config.endDate')}
                    type="date"
                    value={formData?.endDate}
                    onChange={(e) => handleInputChange('endDate', e?.target?.value)}
                    description={t('payBandsPage.config.endDateHint')}
                  />
                </div>
              </div>

              {/* Progression Rules */}
              <div className="space-y-4">
                <h4 className="font-medium text-foreground">Progression Rules</h4>
                
                <Input
                  label={t('payBandsPage.config.annualIncrease')}
                  type="number"
                  placeholder="3.5"
                  value={formData?.progressionRules?.annualIncrease}
                  onChange={(e) => handleNestedChange('progressionRules', 'annualIncrease', e?.target?.value)}
                  description={t('payBandsPage.config.annualIncreaseHint')}
                />

                <Input
                  label={t('payBandsPage.config.maxProgression')}
                  type="number"
                  placeholder="15"
                  value={formData?.progressionRules?.maxProgression}
                  onChange={(e) => handleNestedChange('progressionRules', 'maxProgression', e?.target?.value)}
                  description={t('payBandsPage.config.maxProgressionHint')}
                />

                <Input
                  label={t('payBandsPage.config.perfMultiplier')}
                  type="number"
                  placeholder="1.2"
                  value={formData?.progressionRules?.performanceMultiplier}
                  onChange={(e) => handleNestedChange('progressionRules', 'performanceMultiplier', e?.target?.value)}
                  description={t('payBandsPage.config.perfMultiplierHint')}
                />
              </div>

              {/* Compliance Settings */}
              <div className="space-y-4">
                <h4 className="font-medium text-foreground">Compliance Settings</h4>
                
                <Checkbox
                  label={t('payBandsPage.config.autoFlag')}
                  checked={formData?.complianceSettings?.autoFlag}
                  onChange={(e) => handleNestedChange('complianceSettings', 'autoFlag', e?.target?.checked)}
                  description={t('payBandsPage.config.autoFlagHint')}
                />

                <Input
                  label={t('payBandsPage.config.gapThreshold')}
                  type="number"
                  placeholder="5"
                  value={formData?.complianceSettings?.gapThreshold}
                  onChange={(e) => handleNestedChange('complianceSettings', 'gapThreshold', Number(e?.target?.value))}
                  description={t('payBandsPage.config.gapThresholdHint')}
                />

                <Select
                  label={t('payBandsPage.config.reviewFreq')}
                  options={reviewFrequencyOptions}
                  value={formData?.complianceSettings?.reviewFrequency}
                  onChange={(value) => handleNestedChange('complianceSettings', 'reviewFrequency', value)}
                  description={t('payBandsPage.config.reviewFreqHint')}
                />
              </div>
            </form>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
            <Button variant="ghost" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button 
              variant="default" 
              onClick={handleSubmit}
              loading={isLoading}
              iconName="Save"
              iconPosition="left"
            >
              {band ? 'Update Band' : 'Create Band'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BandConfigPanel;